import React, { useEffect, useState } from 'react';
import { Imovel, TipoImovel } from '../../types/Property';
import { getOrderedImages } from '../../utils/mediaUtils';
import { FavoritoButton } from '../Cliente/FavoritoButton';
import './PropertyCard.css';

interface PropertyCardProps {
  imovel: Imovel;
  onClick?: (imovel: Imovel) => void;
}

export const PropertyCard: React.FC<PropertyCardProps> = ({ imovel, onClick }) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [shareButtonText, setShareButtonText] = useState<string>('Compartilhar');

  const handleClick = () => {
    onClick?.(imovel);
  };

  const handleWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    const phoneNumber = '5547988569815';
    const message = `Olá! Tenho interesse no imóvel ${imovel.titulo} (Cód: ${imovel.codigo})`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const shareUrl = `${window.location.origin}/imovel/${imovel.codigo}`;
      await navigator.clipboard.writeText(shareUrl);
      
      setShareButtonText('✓ Copiado!');
      setTimeout(() => {
        setShareButtonText('Compartilhar');
      }, 2000);
      
      console.log('✅ Link copiado para a área de transferência:', shareUrl);
    } catch (error) {
      console.error('❌ Erro ao copiar link:', error);
      alert('Erro ao copiar link. Tente novamente.');
    }
  };

  const formatPrice = (price: number | string | null | undefined) => {
    const numPrice = Number(price) || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
    }).format(numPrice);
  };

  const getTipoLabel = (tipo: TipoImovel) => {
    const labels: Record<TipoImovel, string> = {
      [TipoImovel.APARTAMENTO]: 'Apartamento',
      [TipoImovel.CASA]: 'Casa',
      [TipoImovel.KITNET]: 'Kitnet',
      [TipoImovel.STUDIO]: 'Studio',
      [TipoImovel.LOFT]: 'Loft',
      [TipoImovel.DUPLEX]: 'Duplex',
      [TipoImovel.COBERTURA]: 'Cobertura',
      [TipoImovel.TERRENO]: 'Terreno',
      [TipoImovel.COMERCIAL]: 'Comercial',
      [TipoImovel.GALPAO]: 'Galpão',
    };
    return labels[tipo] || tipo;
  };

  const getImageUrl = (midiaUrl: string) => {
    if (!midiaUrl) return '';

    const rawBackend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '') || 'http://localhost:3005';
    const rawApiBaseEnv = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
    const apiBase = rawApiBaseEnv || (rawBackend.endsWith('/api') ? rawBackend : `${rawBackend}/api`);

    let origin = '';
    let apiPath = '/api';
    try {
      const u = new URL(apiBase);
      origin = u.origin;
      apiPath = u.pathname.replace(/\/+$/, '') || '/api';
    } catch {
      origin = '';
      apiPath = '/api';
    }

    const rawPath = midiaUrl.startsWith('/') ? midiaUrl : `/${midiaUrl}`;
    const idx = rawPath.toLowerCase().indexOf('/uploads');
    const uploadsPath = idx >= 0 ? rawPath.substring(idx) : rawPath;
    const cleanPath = uploadsPath.replace(/^(\/+)(api\/)+/i, '/');

    const finalUrl = `${origin}${apiPath}${cleanPath}`;
    return finalUrl;
  };

  useEffect(() => {
    const imagens = getOrderedImages(imovel);
    
    if (imagens.length > 0) {
      const imagemAtual = imagens[currentImageIndex];
      const finalUrl = getImageUrl(imagemAtual.url);
      setImageSrc(finalUrl);
    } else {
      setImageSrc('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`
        <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
          <rect width="100%" height="100%" fill="#f8f9fa"/>
          <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="16" fill="#6c757d" text-anchor="middle" dy=".3em">
            Sem imagem
          </text>
        </svg>
      `));
    }
  }, [imovel, currentImageIndex]);

  const imagens = getOrderedImages(imovel);

  const nextImage = () => {
    if (imagens.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % imagens.length);
    }
  };

  const prevImage = () => {
    if (imagens.length > 0) {
      setCurrentImageIndex((prev) => prev === 0 ? imagens.length - 1 : prev - 1);
    }
  };

  return (
    <div className="property-card">
      {/* Seção da Imagem */}
      <div className="property-image-container">
        {/* Badge COD, Botão Favorito e Botão Compartilhar */}
        <div className="property-image-overlay">
          <span className="property-code-badge">Cód: {imovel.codigo}</span>
          <div className="property-actions-buttons">
            <FavoritoButton imovelId={imovel.id} size="medium" />
            <button 
              className={`property-share-btn ${shareButtonText === '✓ Copiado!' ? 'copied' : ''}`}
              onClick={handleShare}
              title="Compartilhar imóvel"
            >
              {shareButtonText}
            </button>
          </div>
        </div>

        {/* Imagem */}
        {imageSrc ? (
          <div className="image-carousel">
            <img
              src={imageSrc}
              alt={imovel.titulo}
              className="property-image"
              crossOrigin="anonymous"
              onClick={handleClick}
              style={{ opacity: 0, transition: 'opacity 0.3s ease', cursor: 'pointer' }}
              onLoad={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.opacity = '1';
              }}
              onError={(e) => {
                console.error('❌ PropertyCard - Erro ao carregar imagem:', {
                  urlTentativa: imageSrc,
                  imovelTitulo: imovel.titulo,
                  erro: e
                });
                (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlbSBu8m8gZW5jb250cmFkYTwvdGV4dD48L3N2Zz4=';
              }}
            />
            
            {imagens.length > 1 && (
              <>
                <button 
                  className="carousel-btn prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                >
                  ‹
                </button>
                <button 
                  className="carousel-btn next"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                >
                  ›
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="property-image" style={{
            width: '100%',
            height: '100%',
            background: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6c757d'
          }}>
            Sem imagem
          </div>
        )}
      </div>

      {/* Seção de Conteúdo */}
      <div className="property-content">
        {/* Métricas principais: Dormitórios, Banheiros, Área */}
        <div className="property-metrics">
          {imovel.tipo_imovel !== TipoImovel.COMERCIAL && (
            <div className="metric-item">
              <svg className="metric-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 12C2 9.79086 3.79086 8 6 8H18C20.2091 8 22 9.79086 22 12V20C22 20.5523 21.5523 21 21 21H19C18.4477 21 18 20.5523 18 20V16H6V20C6 20.5523 5.55228 21 5 21H3C2.44772 21 2 20.5523 2 20V12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span className="metric-label">Dorm {imovel.quartos}</span>
            </div>
          )}
          <div className="metric-item">
            <svg className="metric-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12C9 13.1046 8.10457 14 7 14C5.89543 14 5 13.1046 5 12C5 10.8954 5.89543 10 7 10C8.10457 10 9 10.8954 9 12Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M4 20V12C4 8.68629 6.68629 6 10 6H14C17.3137 6 20 8.68629 20 12V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="metric-label">Ban {imovel.banheiros}</span>
          </div>
          <div className="metric-item">
            <svg className="metric-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 7V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M3 21V19C3 17.8954 3.89543 17 5 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M21 21L3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M21 17H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M15 21V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M9 3V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="metric-label">{imovel.area_util}m²</span>
          </div>
        </div>

        {/* Tipo do imóvel */}
        <div className="property-type-name">{getTipoLabel(imovel.tipo_imovel)}</div>

        {/* Nome do imóvel */}
        <div className="property-title-name">{imovel.titulo}</div>

        {/* Preço */}
        <div className="property-main-price">
          {imovel.valor_aluguel && imovel.valor_aluguel > 0 && imovel.valor_venda && imovel.valor_venda > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div>{formatPrice(imovel.valor_venda)}</div>
              <div>{formatPrice(imovel.valor_aluguel)} / mês</div>
            </div>
          ) : imovel.valor_venda && imovel.valor_venda > 0 ? (
            <div>{formatPrice(imovel.valor_venda)}</div>
          ) : (
            <div>{formatPrice(imovel.valor_aluguel || 0)} / mês</div>
          )}
        </div>

        {/* Localização */}
        <div className="property-location-text">
          {imovel.bairro && `${imovel.bairro}, `}
          {imovel.cidade} - {imovel.estado}
        </div>

        {/* Botão de ação */}
        <button 
          className="property-action-btn" 
          onClick={handleWhatsApp}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="currentColor"/>
          </svg>
          Tirar dúvidas
        </button>
      </div>
    </div>
  );
};